//@ts-nocheck
/**
 * This is the BookingModalPage page component.
 *
 * @namespace Pages
 */

import BookingModal from '../../components/Booking/BookingModal/BookingModal';
import BookingEmptyState from './BookingEmptyState';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { planIsSoldOut, planIsPastDeadline } from '../../utils/booking';
import { initGA, trackPageView } from '../../utils/analytics';
import { Skeleton, CircularProgress, Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOutputTrip } from '../../services/output/trip';
import {
  getTenantSettings,
  getTripSettings
} from '../../services/tenant/tenant';
import { setHighlightColor } from '../../redux/slices/color/color';
import { setHeadingFont, setBodyFont } from '../../redux/slices/font/font';
import { getLocalTenantData } from '../../utils/localTenantData';
import {
  parseTripLogoSetting,
  withResolvedOperatorLogo
} from '../../utils/operatorLogo';
import {
  parseContactSettings,
  withResolvedOperatorContact
} from '../../utils/operatorContact';
import { getTripAssignedDiscountCodes } from '../../utils/discountCodes';
import {
  addBooking,
  updateActiveStep,
  resetPricingSelection
} from '../../redux/slices/booking/booking';

const SETTINGCODE = 'TEMPLATEFIELDS';
const GLOBAL_CONTACT_SETTINGCODE = 'GLOBALCONTACT';
const TRIP_LOGO_SETTINGCODE = 'TRIPLOGOURL';

const BookingModalPage = () => {
  const { t } = useTranslation(['trip', 'common']);
  const { tripid } = useParams();
  const { enableTripPlanVisibility, enableOperatorContactSettings } =
    useFlags();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSpinner, setShowSpinner] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const booking = useSelector((state) => state.booking);
  const tenantData = getLocalTenantData();
  const pricingPlans = useSelector(
    (state) => state?.booking?.bookingplans || []
  );

  const getVisibilityStatus = (pricingPlan = {}, tripdData = {}) => {
    const { capacity: tripCapacity, usedCapacity } = tripdData;
    const {
      ishiddenfrompassengers,
      autohidewhensoldout,
      autohideafterdeadline
    } = pricingPlan;

    if (enableTripPlanVisibility) {
      // Manual hide takes priority
      if (ishiddenfrompassengers) {
        return { hidden: true, reason: 'manual', label: t('common:hidden') };
      }

      // Check sold out condition
      if (autohidewhensoldout) {
        // Check plan-level sold out
        const planSoldOut = planIsSoldOut(pricingPlan);

        // Check trip-level sold out (if trip capacity is set)
        const tripSoldOut =
          tripCapacity !== null &&
          tripCapacity !== undefined &&
          tripCapacity - usedCapacity <= 0;

        if (planSoldOut || tripSoldOut) {
          return {
            hidden: true,
            reason: 'soldout',
            label: t('trippricing:soldOut')
          };
        }
      }

      // Check deadline condition
      if (autohideafterdeadline) {
        if (planIsPastDeadline(pricingPlan)) {
          return {
            hidden: true,
            reason: 'deadline',
            label: t('trippricing:bookingDeadlinePast')
          };
        }
      }
    }

    return { hidden: false, reason: null, label: null };
  };

  const sortTripPlans = (tripData) => {
    const tripPlans = tripData?.tripplan || [];

    if (Array.isArray(tripPlans)) {
      const filteredTripPlans = tripPlans?.filter((plan) => {
        const { hidden } = getVisibilityStatus(plan, tripData);

        return !hidden;
      });

      const sortedTripPlans = filteredTripPlans?.sort((a, b) => {
        return Number(a?.seq) - Number(b?.seq);
      });

      return sortedTripPlans.map((tripPlan) => {
        return {
          ...tripPlan,
          selectedQuantity: 0,
          depositamount: Number(tripPlan?.depositamount),
          maxavailable: Number(tripPlan?.maxavailable),
          priceperperson: Number(tripPlan?.priceperperson),
          isSelected: false
        };
      });
    }

    return [];
  };

  const onOpenBookingModal = (open = false) => {
    if (open) {
      if (booking.bookingplans?.length) {
        dispatch(updateActiveStep({ key: 'plan' }));
        dispatch(resetPricingSelection());
        setShowBookingModal(open);
      }
    } else {
      navigate(-1);
      setShowBookingModal(open);
    }
  };

  useEffect(() => {
    const fetchOperatorContactSettings = async () => {
      if (!enableOperatorContactSettings) {
        return {};
      }

      const [tripContactResult, globalContactResult] = await Promise.allSettled(
        [
          getTripSettings(tripid, SETTINGCODE),
          getTenantSettings(GLOBAL_CONTACT_SETTINGCODE)
        ]
      );

      return {
        tripContactSettings:
          tripContactResult.status === 'fulfilled'
            ? parseContactSettings(tripContactResult.value?.data?.settingvalue)
            : {},
        globalContactSettings:
          globalContactResult.status === 'fulfilled'
            ? parseContactSettings(
                globalContactResult.value?.data?.settingvalue
              )
            : {}
      };
    };

    const fetchTripLogoUrl = async () => {
      if (!enableOperatorContactSettings) {
        return '';
      }

      try {
        const tripLogoSettings = await getTripSettings(
          tripid,
          TRIP_LOGO_SETTINGCODE
        );

        return parseTripLogoSetting(tripLogoSettings?.data?.settingvalue);
      } catch (error) {
        return '';
      }
    };

    const fetchData = async () => {
      try {
        const { data } = await getOutputTrip(tripid);

        if (data && Object.keys(data).length > 0) {
          const [operatorContactSettings, tripLogoUrl] = await Promise.all([
            fetchOperatorContactSettings(),
            fetchTripLogoUrl()
          ]);
          const contactResolvedData = withResolvedOperatorContact(data, {
            ...operatorContactSettings,
            enabled: enableOperatorContactSettings
          });
          const resolvedData = withResolvedOperatorLogo(contactResolvedData, {
            tripLogoUrl,
            enabled: enableOperatorContactSettings
          });

          dispatch(updateActiveStep({ key: 'plan' }));

          const bookingPayload = {
            tenant: resolvedData?.tenant || tenantData,
            bookingplans: sortTripPlans(resolvedData),
            addons: resolvedData?.tripaddon ?? [],
            trip: {
              tripname: resolvedData?.tripname,
              startdate: resolvedData?.startdate,
              duration: resolvedData?.duration,
              enddate: resolvedData?.enddate,
              tripid: resolvedData?.tripid,
              tripkey: resolvedData?.tripkey,
              fees: resolvedData?.fees ?? [],
              capacity: resolvedData?.capacity ?? null,
              requiresSignedDocument:
                resolvedData?.tripsignabledocument?.[0]?.required ?? false,
              signedDocumentTemplateId:
                resolvedData?.tripsignabledocument?.[0]?.signabledocument
                  ?.templateid ?? null,
              operatorEmail: resolvedData?.operatorContact?.email,
              operatorContact: resolvedData?.operatorContact,
              paymentplan: resolvedData?.paymentplan,
              tripimage: resolvedData?.tripimage ?? [],
              discountCodes: getTripAssignedDiscountCodes(resolvedData)
            },
            usedCapacity: resolvedData?.usedCapacity ?? 0
          };

          dispatch(addBooking(bookingPayload));

          // Update page title
          const tripName = resolvedData?.tripname;
          const tenantName = resolvedData?.tenant?.tenantname;
          if (tripName && tenantName) {
            document.title = `${tripName} - ${tenantName}`;
          }

          // Initialize GA and track page view for embedded booking page
          const gaId = resolvedData?.tenant?.googleanalyticsid;
          if (gaId) {
            const currency =
              resolvedData?.tenant?.sellingcurrency?.currencycode ||
              resolvedData?.tenant?.basecurrency?.currencycode;
            initGA(gaId);
            trackPageView(`/shared/trip/${resolvedData?.tripid}/booking`, {
              page_title:
                tripName && tenantName
                  ? `${tripName} - ${tenantName}`
                  : undefined,
              trip_id: resolvedData?.tripid,
              trip_name: resolvedData?.tripname,
              currency
            });
          }
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
        setTimeout(() => setShowSpinner(false), 500); // Hide spinner and show skeleton after delay
      }
    };

    const fetchTemplateSettings = async () => {
      try {
        const settingsData = await getTenantSettings(SETTINGCODE);
        if (!settingsData?.data || !settingsData?.data?.settingvalue) {
          dispatch(setHeadingFont('Inter'));
          dispatch(setBodyFont('Inter'));
          dispatch(setHighlightColor('#0D69D4'));
        }
        if (settingsData?.data?.settingvalue) {
          const settingvalue = JSON.parse(
            settingsData?.data?.settingvalue
          )?.[0];
          const { headingfont, bodyfont, color } = settingvalue;
          dispatch(setHeadingFont(headingfont ?? 'Inter'));
          dispatch(setBodyFont(bodyfont ?? 'Inter'));
          dispatch(setHighlightColor(color ?? '#0D69D4'));
        }
      } catch (error) {
        console.error(t('common:errorLoadingSettings'), error);
      }
    };

    if (
      enableTripPlanVisibility !== undefined &&
      enableOperatorContactSettings !== undefined
    ) {
      fetchData();
      fetchTemplateSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    tripid,
    enableTripPlanVisibility,
    enableOperatorContactSettings
  ]);

  //This effect add a transparent background to the iframe
  useEffect(() => {
    document.documentElement.style.backgroundColor = 'transparent';
  }, []);

  const handleCloseIframe = (event) => {
    event.stopPropagation();
    event.preventDefault();
    //Send a message to the parent window(script listener)
    window.parent.postMessage({ action: 'close' }, '*');
  };

  const addCloseEventListener = useCallback((element) => {
    if (element) {
      element.addEventListener('click', handleCloseIframe);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const closeButton = document.querySelector('[data-xgcl-stng-close]');

      if (closeButton) {
        clearInterval(interval);
        addCloseEventListener(closeButton);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [addCloseEventListener]);

  useEffect(() => {
    const interval = setInterval(() => {
      const cancelButton = document.querySelector('[data-xgfe-stng-close]');

      if (cancelButton) {
        clearInterval(interval);
        addCloseEventListener(cancelButton);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [addCloseEventListener]);

  if (loading) {
    if (showSpinner) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100%'
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100%'
        }}
      >
        <Skeleton
          variant='rectangular'
          width={'100%'}
          height={1000}
          sx={{ border: '1px solid #EAECF0' }}
        />
      </Box>
    );
  }

  if (error) return <p>Error: {error.message}</p>;

  return (
    <>
      {enableTripPlanVisibility ? (
        <div>
          {pricingPlans?.length > 0 ? (
            <BookingModal
              id='book-now-modal'
              isOpenModal={showBookingModal}
              updateIsOpen={onOpenBookingModal}
            />
          ) : (
            <BookingEmptyState
              isOpenModal={showBookingModal}
              updateIsOpen={onOpenBookingModal}
            />
          )}
        </div>
      ) : (
        <div>
          <BookingModal
            id='book-now-modal'
            isOpenModal={showBookingModal}
            updateIsOpen={onOpenBookingModal}
          />
        </div>
      )}
    </>
  );
};

export default BookingModalPage;
